import React from 'react';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';
import { MemberEquipmentModal } from './MemberEquipmentModal';

export interface EditableEquipmentItem {
  id: string;
  name: string;
  category?: string;
  description?: string | null;
  modifiers?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onUse?: () => void;
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface MemberEquipmentEditorProps {
  items: EditableEquipmentItem[];
  editMode?: boolean;
  onAdd?: () => void;
  onEdit?: () => void;
  modalMemberName?: string;
  modalAvailableItems?: EditableEquipmentItem[];
  modalSelectedItems?: EditableEquipmentItem[];
  onModalChange?: (selectedIds: string[]) => void;
  title?: string;
}

export const MemberEquipmentEditor: React.FC<MemberEquipmentEditorProps> = ({
  items,
  editMode = false,
  onAdd,
  onEdit,
  title = 'Equipment',
  modalMemberName,
  modalAvailableItems,
  modalSelectedItems,
  onModalChange,
}) => {
  const [details, setDetails] = React.useState<PopoverState | null>(null);
  const [pendingRemoval, setPendingRemoval] = React.useState<EditableEquipmentItem | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  const openDetails = (event: React.MouseEvent<HTMLElement>, item: EditableEquipmentItem) => {
    if (item.onClick) {
      item.onClick(event);
      return;
    }
    setDetails({
      anchor: event.currentTarget,
      id: item.id,
      name: item.name,
      description: item.description ?? null,
      tags: [],
      modifiers: item.modifiers,
    });
  };

  return <Box sx={{ mb: 3 }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
      {editMode && onAdd && <IconButton size="small" aria-label={`Add ${title.toLowerCase()}`} onClick={onAdd}><AddIcon /></IconButton>}
      {editMode && modalAvailableItems && onModalChange && <IconButton size="small" aria-label={`Edit ${title.toLowerCase()}`} onClick={() => setModalOpen(true)}><EditIcon fontSize="small" /></IconButton>}
      {editMode && onEdit && <IconButton size="small" aria-label={`Edit ${title.toLowerCase()}`} onClick={onEdit}><EditIcon fontSize="small" /></IconButton>}
    </Stack>
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary">None</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {items.map((item) => <Chip
          key={item.id}
          size="small"
          label={item.name}
          clickable
          onClick={(event) => openDetails(event, item)}
          onDelete={editMode && item.onRemove ? () => setPendingRemoval(item) : undefined}
        />)}
      </Box>
    )}
    <ItemDetailsPopover popover={details} onClose={() => setDetails(null)} />
    {modalAvailableItems && onModalChange && <MemberEquipmentModal
      open={modalOpen}
      memberName={modalMemberName ?? ''}
      availableItems={modalAvailableItems}
      selectedItems={modalSelectedItems ?? items}
      onClose={() => setModalOpen(false)}
      onChange={onModalChange}
    />}
    <ConfirmActionDialog
      open={pendingRemoval !== null}
      title={`Remove ${pendingRemoval?.name ?? 'equipment'}`}
      message={pendingRemoval ? `Remove "${pendingRemoval.name}"?` : ''}
      confirmLabel={pendingRemoval?.removeLabel ?? 'Remove'}
      confirmColor="error"
      onClose={() => setPendingRemoval(null)}
      onConfirm={() => {
        pendingRemoval?.onRemove?.();
        setPendingRemoval(null);
      }}
    />
  </Box>;
};
